/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable, NgZone } from '@angular/core';

import { environment } from '@environments/environment';
import { getFirebaseConfig, getGoogleClientID } from '@app/shared/utils/url.utils';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import { Observable, Observer } from 'rxjs';
import { MessageRegistrationCheckerService } from './message-registration-checker.service';
import { Params } from '@angular/router';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithCredential,
  User,
} from 'firebase/auth';

import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import {
  analyticsGet,
  analyticsIsSupported,
  analyticsSetUserProperties,
  authGet,
  firebaseInitializeApp,
  messagingGet,
  messagingIsSupported,
  remoteConfigGet,
  remoteConfigIsSupported,
} from '@app/shared/firebase/firebase.wrapper';
import { AkitaFirebaseService } from '../state/firebase.service';

const USER_ID_KEY = 'user_id';

const GTAG_SET_COMMAND = 'set';
const GTAG_EVENT_COMMAND = 'event';
const GTAG_CONFIG_COMMAND = 'config';
const GTAG_CONSENT_COMMAND = 'consent';

const PRODUCT_PAGE_VIEW_AD_CONVERSION = 'aUHgCPrrxuUBEMPj2b0D';
const ADD_TO_CART_AD_CONVERSION = '0WOzCNzTxuUBEMPj2b0D';
const COMPLETE_ADDRESS_AND_PHONE_AD_CONVERSION = 'fY35CJqizuUBEMPj2b0D';
const PAYMENT_METHOD_SELECTED_AD_CONVERSION = 'ieKdCPWvzuUBEMPj2b0D';
const PURCHASE_AD_CONVERSION = 'Hk2pCJjzvOUBEMPj2b0D';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  public static FCM_LINKED_TO_SERVICE_WORKER: boolean;
  private firebaseApp: any | null = null;
  private firebaseAuth: any | null = null;
  private readonly firebaseAnalytics: any | null = null;
  private firebaseConfig: any | null = null;
  private readonly extraAnalyticsKey: string | null = null;
  private readonly adsTrackingKey: string | null = null;
  private readonly gtmTrackingKey: string | null = null;

  private shouldShowOneTap = true;

  /*   private readonly currentUserSubject = new BehaviorSubject(null as any);
   */
  constructor(
    private readonly zone: NgZone,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly messageRegistrationCheckerService: MessageRegistrationCheckerService,
    private readonly akitaFirebaseService: AkitaFirebaseService
  ) {
    this.firebaseApp = this.app;
    this.firebaseConfig = getFirebaseConfig();
    this.initFirebase();

    this.setDefaultAdsConsent();

    try {
      if ((window as any).POPSY_EXTRA_ANALYTICS_KEY) {
        this.extraAnalyticsKey = (window as any).POPSY_EXTRA_ANALYTICS_KEY || null;
      }
      if ((window as any).POPSY_ADS_TRACKING_KEY) {
        this.adsTrackingKey = (window as any).POPSY_ADS_TRACKING_KEY || null;
      }
      if ((window as any).POPSY_GTM_TRACKING_KEY) {
        this.gtmTrackingKey = (window as any).POPSY_GTM_TRACKING_KEY || null;
      }
    } catch (error) {}
  }

  public getFirebaseAuth(): any | null {
    return this.firebaseAuth;
  }

  private setDefaultAdsConsent(): void {
    console.log('Firebase consent update:', this.firebaseConfig.measurementId);

    try {
      console.log('Firebase consent updated:', this.firebaseConfig.measurementId);

      if (this.firebaseConfig?.measurementId && gtag) {
        gtag(GTAG_CONSENT_COMMAND, 'default', {
          ad_storage: 'granted',
          analytics_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        } as any);

        gtag(GTAG_CONSENT_COMMAND, 'update', {
          ad_storage: 'granted',
          analytics_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        } as any);
      }

      if (this.firebaseAnalytics) {
        analyticsSetUserProperties(
          this.firebaseAnalytics,
          {
            consent_ads: 'granted',
            consent_analytics: 'granted',
          },
          null
        );
      }

      gtag('get', this.firebaseConfig.measurementId, 'consent_state', (consentState) => {
        console.log('Consent state:', consentState);
      });
    } catch (error) {}
  }

  public updateConfig(
    config: Gtag.ControlParams | Gtag.EventParams | Gtag.ConfigParams | Gtag.CustomParams
  ): void {
    try {
      if (this.firebaseConfig?.measurementId && gtag) {
        gtag(GTAG_CONFIG_COMMAND, this.firebaseConfig.measurementId, {
          ...config,
          update: true,
        });
      }

      if (this.extraAnalyticsKey && gtag) {
        gtag(GTAG_CONFIG_COMMAND, this.extraAnalyticsKey, {
          ...config,
          update: true,
        });
      }

      if (this.adsTrackingKey && gtag) {
        gtag(GTAG_CONFIG_COMMAND, this.adsTrackingKey, {
          ...config,
          update: true,
        });
      }

      if (this.gtmTrackingKey && gtag) {
        gtag(GTAG_CONFIG_COMMAND, this.gtmTrackingKey, {
          ...config,
          update: true,
        });
      }
    } catch (error) {}
  }

  private recordADConversion(id: string, params?: Params | null): void {
    if (this.adsTrackingKey && gtag) {
      gtag('event', 'conversion', {
        ...(params || {}),
        send_to: `${this.adsTrackingKey}/${id}`,
      });
    }
  }

  public get recordProductPageViewADConversion(): () => void {
    return () => {
      this.recordADConversion(PRODUCT_PAGE_VIEW_AD_CONVERSION);
    };
  }

  public recordAddToCartADConversion(): void {
    this.recordADConversion(ADD_TO_CART_AD_CONVERSION);
  }

  public recordAddressAndPhoneADConversion(): void {
    this.recordADConversion(COMPLETE_ADDRESS_AND_PHONE_AD_CONVERSION, {
      value: 0.0,
      currency: 'USD',
    });
  }

  public recordPaymentMethodSelectedADConversion(): void {
    this.recordADConversion(PAYMENT_METHOD_SELECTED_AD_CONVERSION, {
      value: 1.0,
      currency: 'USD',
    });
  }

  public recordPurchaseADConversion(
    deliveryID: string,
    value?: number | null,
    currency?: string | null
  ): void {
    let conversion = {
      value: 1.0,
      currency: 'USD',
    };

    if (value || value === 0) {
      conversion = {
        value: value,
        currency: currency || 'USD',
      };
    }

    this.recordADConversion(PURCHASE_AD_CONVERSION, {
      value: conversion.value,
      currency: conversion.currency,
      transaction_id: deliveryID,
    });
  }

  public registerFCMInOwnFirebaseServiceWorker(): Observable<void> {
    // Firebase will use it's own SW in the next step, this empty method is just to keep the same flow
    return new Observable((observer: Observer<void>) => {
      this.messageRegistrationCheckerService.markAsRegistered();
      observer.next();
      observer.complete();
    });
  }

  public setUserId(id: string | null): void {
    this.updateConfig({
      [USER_ID_KEY]: id,
    });

    try {
      if (gtag) {
        gtag(GTAG_SET_COMMAND, {
          user_id: `${id}`,
        });
      }
    } catch (error) {}
  }

  public logEventToAnalytics(
    name: Gtag.EventNames | string,
    eventParams?: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams,
    category?: string | null,
    label?: string | null
  ): void {
    try {
      if (this.extraAnalyticsKey && gtag) {
        gtag(GTAG_EVENT_COMMAND, name, {
          ...(eventParams || {}),
          event_category: category || 'Custom',
          event_label: label || 'User Action',
          send_to: `${this.extraAnalyticsKey}`,
        });
      }

      if (this.gtmTrackingKey && gtag) {
        gtag(GTAG_EVENT_COMMAND, name, {
          ...(eventParams || {}),
          send_to: `${this.gtmTrackingKey}`,
        });
      }
    } catch (error) {}
  }

  private initializeOrRehuseApp(currentApp?: any | null, config?: any | null): any {
    if (currentApp) {
      return currentApp;
    }

    return firebaseInitializeApp(config || environment.firebase);
  }

  private get app(): any | null {
    try {
      if (!this.firebaseConfig) {
        this.firebaseConfig = environment.firebase;
      }

      this.firebaseApp = this.initializeOrRehuseApp(
        this.firebaseApp,
        this.firebaseConfig
      );

      SentryUtil.addObjectKeysToScope('firebase', {
        app: `${this.firebaseApp?.name || '?'}`,
        ready: 'YES',
      });

      return this.firebaseApp;
    } catch (error) {
      SentryUtil.addObjectKeysToScope('firebase', {
        app: `-`,
        ready: 'NO',
      });
      console.warn(error);
      return null;
    }
  }

  public get analytics(): Promise<any | null> {
    return new Promise(
      (
        resolve: (messaging: any | null) => void,
        reject: (reason?: any | null) => void
      ) => {
        if (this.akitaRouterQuery.isBrowser) {
          this.zone.runOutsideAngular(() => {
            analyticsIsSupported()
              .then((isSupported?: boolean | null) => {
                if (isSupported) {
                  if (this.firebaseAnalytics) {
                    resolve(this.firebaseAnalytics);
                  } else {
                    try {
                      const analyticsInstance = analyticsGet(this.app || undefined);
                      resolve(analyticsInstance);
                    } catch (error: unknown) {
                      reject(error);
                    }
                  }
                } else {
                  resolve(null);
                }
              })
              .catch((error: unknown) => {
                reject(error);
              });
          });
        } else {
          resolve(null);
        }
      }
    );
  }

  public get remoteConfig(): Promise<any | null> {
    return new Promise(
      (
        resolve: (messaging: any | null) => void,
        reject: (reason?: any | null) => void
      ) => {
        if (this.akitaRouterQuery.isBrowser) {
          this.zone.runOutsideAngular(() => {
            remoteConfigIsSupported()
              .then((isSupported?: boolean | null) => {
                if (isSupported) {
                  try {
                    const remoteConfigInstance = remoteConfigGet(this.app || undefined);
                    resolve(remoteConfigInstance);
                  } catch (error: unknown) {
                    reject(error);
                  }
                } else {
                  resolve(null);
                }
              })
              .catch((error: unknown) => {
                reject(error);
              });
          });
        } else {
          resolve(null);
        }
      }
    );
  }

  public get auth(): any | null {
    try {
      if (this.akitaRouterQuery.isBrowser) {
        const auth = this.firebaseAuth || authGet(this.app || undefined);

        auth.useDeviceLanguage();
        return auth;
      }
      return null;
    } catch (error) {
      // SSR / Tests not supported
      return null;
    }
  }

  public refreshAuth(): void {
    if (this.firebaseAuth) {
      this.initFirebase();
    } else {
      console.warn('Firebase Auth not initialized, cannot refresh.');
    }
  }

  private initFirebase(): void {
    this.firebaseApp = this.initializeOrRehuseApp(this.firebaseApp, this.firebaseConfig);
    this.firebaseAuth = getAuth(this.firebaseApp);

    onIdTokenChanged(this.firebaseAuth, async (user) => {
      if (user) {
        await user.getIdToken(); // automatically up-to-date
        // Optionally update your local store or re-send to your backend
      }
    });

    onAuthStateChanged(this.firebaseAuth, async (user) => {
      if (user) {
        try {
          await user.getIdToken();

          /* this.akitaFirebaseService.setToken(token); // update Akita store */
          this.akitaFirebaseService.setCurrentUser(user); // update Akita store
          this.shouldShowOneTap = false;
        } catch (error) {
          console.error('❌ Error getting token:', error);
        }
      } else {
        this.akitaFirebaseService.clearStore(); // Clear Akita store on sign out
        if (!this.shouldShowOneTap) {
          // this.initializeOneTapIfNeeded();
          // Initialize Google One Tap if needed
        }
      }
    });
  }

  public getToken(): Promise<string | null> {
    const user = this.firebaseAuth?.currentUser;
    if (user) {
      return user.getIdToken();
    }
    return Promise.resolve(null);
  }

  public getCurrentUser(): User | null {
    return this.firebaseAuth?.currentUser ?? null;
  }

  public get messaging(): Promise<any | null> {
    return new Promise(
      (
        resolve: (messaging: any | null) => void,
        reject: (reason?: any | null) => void
      ) => {
        if (this.akitaRouterQuery.isBrowser) {
          this.zone.runOutsideAngular(() => {
            messagingIsSupported()
              .then((isSupported?: boolean | null) => {
                if (isSupported) {
                  try {
                    const messagingInstance = messagingGet(this.app || undefined);
                    resolve(messagingInstance);
                  } catch (error: unknown) {
                    reject(error);
                  }
                } else {
                  resolve(null);
                }
              })
              .catch((error: unknown) => {
                reject(error);
              });
          });
        } else {
          resolve(null);
        }
      }
    );
  }

  public initializeOneTapIfNeeded() {
    // this.oneTapInitialized = true;
    // Now initialize Google One Tap
    const response = (google as any).accounts.id.initialize({
      client_id: getGoogleClientID(),
      callback: this.handleOneTapResponse.bind(this),
      state_cookie_domain: 'popsy.app',
      nonce: 'd9d9253bea11db87f25823b719ea39ee',
      cancel_on_tap_outside: false,
      auto_select: false,
      use_fedcm_for_prompt: 'true',
    });

    if (response) {
      (google as any).accounts.id.prompt(); // Trigger One Tap
    }
  }

  private handleOneTapResponse(response: any) {
    const credential = response.credential;

    const firebaseCredential = GoogleAuthProvider.credential(credential);

    signInWithCredential(this.auth, firebaseCredential)
      .then(() => {})
      .catch((error) => {
        console.error('Error signing in with Google One Tap:', error);
      });
  }
}
